/* eslint-disable camelcase */
import { useCallback, useEffect, useState } from 'react';
import { apiV2Instance } from '../../../common/lib/api-v2.client';
import { SignupUpgradeOffer } from '../signup-state';

export type CreditScope = 'SINGLE' | 'DUAL';

export interface GiftHoldingLine {
  id: string;
  senderName: string;
  scope: CreditScope;
  countLeft: number;
  daysLeft: number | null;
  shareDeadline?: string | null;
  createdAt?: string | null;
}

export interface LifetimeCreditsDashboard {
  inventory: {
    singleAvailable: number;
    dualAvailable: number;
    singleGifted?: number;
    dualGifted?: number;
    singleComplementary?: number;
    dualComplementary?: number;
    complementarySingleDaysLeft?: number | null;
    complementaryDualDaysLeft?: number | null;
  };
  signup?: {
    profileBadges?: Array<{ category: string; name: string; threshold?: number }>;
    badgeHistory?: Array<{
      series: string;
      threshold: number;
      cards: number;
      name: string;
      upgradedFrom?: string;
    }>;
    upgradeOffer?: SignupUpgradeOffer | null;
  } | null;
  holdings?: GiftHoldingLine[];
  personalEntitlement: {
    lifetime_pass_type: 'NONE' | 'SINGLE' | 'DUAL';
    single_activated_platform: 'IOS' | 'ANDROID' | null;
    label: string;
    dualPopupSeenWeb: boolean;
  };
  pendingSentCount: number;
  pendingReceivedCount: number;
}

export interface LifetimeContact {
  id: string;
  name: string;
  email: string;
  mobile: string;
  contactSource?: string;
  hidden: boolean;
  member: {
    id: string;
    username?: string;
    channelName?: string;
    lifetime_pass_type: 'NONE' | 'SINGLE' | 'DUAL';
    joinedAt?: string | null;
  } | null;
  eligibility: string;
  disabledReason: 'name_only' | 'self' | 'already_dual' | null;
  bindMode: 'email' | 'user' | 'token' | null;
}

export interface PendingGift {
  kind: 'invite' | 'transfer';
  id: string;
  scope: CreditScope;
  status: string;
  senderChannel?: string;
  recipientChannel?: string;
  contactName?: string;
  boundEmail?: string;
  expiresAt?: string | null;
  message?: string;
  clickedAt?: string | null;
  signedUpAt?: string | null;
  bindMode?: string;
  quantity?: number;
  redeemedCount?: number;
  extrasRemaining?: number;
  inviteUrl?: string | null;
  shareDeadline?: string | null;
  extrasShareDays?: number | null;
  boundMobile?: string;
  holdingId?: string | null;
  source?: 'mine' | 'gifted';
  daysLeft?: number | null;
  deliveredToInventory?: boolean;
  bucket?: 'pending' | 'partly_redeemed' | 'redeemed' | 'cancelled';
  firstName?: string;
  lastName?: string;
  channelName?: string;
  avatar?: string;
  referralCount?: number;
  subscriberCount?: number;
  created?: string | null;
  ownPassOutcome?: string | null;
  returnReason?: string;
}

export interface ReceivedLists {
  new: PendingGift[];
  accepted: PendingGift[];
  declined: PendingGift[];
}

export interface SentLists {
  pending: PendingGift[];
  partlyRedeemed: PendingGift[];
  redeemed: PendingGift[];
  cancelled: PendingGift[];
}

const EMPTY_SENT: SentLists = {
  pending: [],
  partlyRedeemed: [],
  redeemed: [],
  cancelled: [],
};

const EMPTY_RECEIVED: ReceivedLists = {
  new: [],
  accepted: [],
  declined: [],
};

export const useLifetimeCredits = () => {
  const [dashboard, setDashboard] = useState<LifetimeCreditsDashboard | null>(null);
  const [sentLists, setSentLists] = useState<SentLists>(EMPTY_SENT);
  const [receivedLists, setReceivedLists] = useState<ReceivedLists>(EMPTY_RECEIVED);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, sent, received] = await Promise.all([
        apiV2Instance.get('/api/lifetime-access-credits/dashboard'),
        apiV2Instance.get('/api/lifetime-access-credits/sent'),
        apiV2Instance.get('/api/lifetime-access-credits/received'),
      ]);
      setDashboard(dash.data);
      setSentLists(sent.data?.pending ? {
        pending: sent.data.pending || [],
        partlyRedeemed: sent.data.partlyRedeemed || [],
        redeemed: sent.data.redeemed || [],
        cancelled: sent.data.cancelled || [],
      } : EMPTY_SENT);
      const receivedBody = received.data || {};
      setReceivedLists({
        new: Array.isArray(receivedBody.new) ? receivedBody.new : [],
        accepted: Array.isArray(receivedBody.accepted) ? receivedBody.accepted : [],
        declined: Array.isArray(receivedBody.declined) ? receivedBody.declined : [],
      });
    } catch (err: any) {
      setError(err?.data?.message || err?.response?.data?.message || err?.message || 'Failed to load LA Pass Gift Cards.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { dashboard, sentLists, receivedLists, loading, error, refresh };
};
